import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnPanel, PageBreak, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { ChartType, CommonChartUtil } from '@ddt-web/chart';
import { CURRENCY_SYMBOLS } from '@ddt-web/common';
import { ColorsConfig } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { CellUtil, ComponentData, TabData, TabUtil } from '@ddt-web/data-service';
import { NumberUtil } from '@ddt-web/generic-utils';
import { DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES, PortfolioDotRenderer, ReportUtils } from '@ddt-web/pdf-utils';
import { HypotheticalGrowthValueService } from '@ddt-web/performance';
import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as utc from 'dayjs/plugin/utc';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

import { DisclosureComponent } from '../../disclaimers/disclosure-component';
import { HeadingWithDateComponent } from '../../heading/heading-with-date-component';
import { HighchartsSVGComponent } from '../../highchart/highchart-svg-component';
import { PerformanceDisclaimerUtil } from '../util/performance-disclaimer.util';
import { PerformanceTableUtil } from '../util/performance-table.util';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export class HypotheticalPerformanceGrowthComponent extends BusinessComponent {
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = false;

	// Charts
	private hypoGrossPerfChartTabData: TabData;
	private hypoNetCustomPerfChartTabData: TabData;

	private isHypoGrossChartValidData: boolean = false;
	private isHypoNetCustomChartValidData: boolean = false;
	private shouldShowHypotheticalPerformance: boolean = false;

	private displayCurrency: string = CURRENCY_SYMBOLS.USD;

	private groupedLegendColumnConfiguration: Array<{}>;

	constructor(
		name: string,
		public dataComponentId: string,
		public currencyISO?: string,
		public disclaimerTextClasses?: string[],
		public displayPerformanceHeader = true,
		public shouldStartFromNewPage: boolean = false,
		public yAxisLabel: string = null,
		public customAdvisoryFee: number = null,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
		this.displayCurrency = !isEmpty(currencyISO) ? get(CURRENCY_SYMBOLS, currencyISO, currencyISO) : CURRENCY_SYMBOLS.USD;
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		// chart data
		this.hypoGrossPerfChartTabData = componentData && TabUtil.getVisibleTabData(componentData.getTabById('hypo-chart'));
		this.hypoNetCustomPerfChartTabData = this.isCustomFeeEnabled && !!this.customAdvisoryFee && componentData ?
			TabUtil.getVisibleTabData(componentData.getTabById('hypo-custom-net-chart')) : null;

		// gross validity check
		this.isHypoGrossChartValidData = CellUtil.isValidCellData(this.hypoGrossPerfChartTabData) && !this.hypoGrossPerfChartTabData.error &&
			get(this.hypoGrossPerfChartTabData, 'displayConfig.visible', false);

		// net custom validity check
		this.isHypoNetCustomChartValidData = CellUtil.isValidCellData(this.hypoNetCustomPerfChartTabData) && !this.hypoNetCustomPerfChartTabData.error &&
			get(this.hypoNetCustomPerfChartTabData, 'displayConfig.visible', false);

		this.shouldShowHypotheticalPerformance = this.isCustomFeeEnabled && this.customAdvisoryFee ?
			this.isHypoGrossChartValidData && this.isHypoNetCustomChartValidData : this.isHypoGrossChartValidData;

		return componentData?.success && this.shouldShowHypotheticalPerformance;
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService, applicationState?: any): AbstractRenderer<SVGGElement>[] {
		const performanceState = get(applicationState, 'performanceState', { hypotheticalGrowthMultiplier: 10000 });
		const panels: AbstractRenderer<SVGGElement>[] = [];

		this.groupedLegendColumnConfiguration = PerformanceTableUtil.getPerformanceChartGroupedLegendColumnConfigurationPdf(contentSvc, applicationState);

		if (this.isComponentDataVisible(componentData)) {
			if (this.shouldStartFromNewPage) {
				panels.push(new PageBreak());
			}

			const dcrKeyPrefix = ReportUtils.createDcrKeyPrefixForPdf(this.name);
			const asOfDate = get(componentData.getTabById('hypo-perf-as-of-date'), 'tableData[0].cells[0][0].formattedValue');

			this.updatePanelsWithPerformanceCharts(panels, dcrKeyPrefix, asOfDate, performanceState);
			this.updatePanelsWithPerformanceDisclosures(panels, dcrKeyPrefix);
		}

		return panels;
	}

	private updatePanelsWithPerformanceCharts(
		panels: Array<AbstractRenderer<SVGGElement>>,
		dcrKeyPrefix: string,
		asOfDate: string,
		performanceState: any
	): void {
		panels.push(
			...this.buildHypoPerfChartPanelItems(
				[this.hypoGrossPerfChartTabData, this.hypoNetCustomPerfChartTabData].filter(tab => tab),
				dcrKeyPrefix,
				asOfDate,
				performanceState
			)
		);

	}

	private updatePanelsWithPerformanceDisclosures(
		panels: Array<AbstractRenderer<SVGGElement>>,
		dcrKeyPrefix: string
	): void {
		if (this.isCustomFeeEnabled) {
			if (!this.customAdvisoryFee) {
				panels.push(
					new DisclosureComponent(`NO_NET_PERF_DISCLOSURE`, 'NO_NET_PERF_DISCLOSURE', {},
						{ paddingTop: 5, paddingBottom: 10 }, DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES)
				);
			}
			panels.push(...PerformanceDisclaimerUtil.customFeeEnabledDisclaimer('HYPOTHETICAL_NET_PERFORMANCE_DISCLAIMER_PDF_'));
		} else {
			panels.push(
				PerformanceDisclaimerUtil.noCustomFeeEnabledDisclaimer(dcrKeyPrefix + 'DISCLAIMER', this.disclaimerTextClasses)
			);
		}
	}

	private buildHypoPerfChartPanelItems(tabData: TabData[], dcrKeyPrefix: string, asOfDate: string, performanceState: any): Array<AbstractRenderer<SVGGElement>> {
		const panels: AbstractRenderer<SVGGElement>[] = [];
		panels.push(
			new HeadingWithDateComponent().buildHeader('Hypo Perf Chart Header', 'PDF_HYPO_PERF_CHART_H2', get(tabData?.[0], 'config.title.label', '').defaultText, asOfDate)
		);

		let customAttributes: any = {
			growthMultiplier: performanceState.hypotheticalGrowthMultiplier,
			displayCurrency: this.displayCurrency,
			plotOptions: {
				series: {
					lineWidth: 1.3
				}
			},
			yAxis: {
				title: {
					text: this.yAxisLabel
				}
			}
		};
		if (this.isCustomFeeEnabled) {
			customAttributes = {
				chart: { marginBottom: 40 },
				growthMultiplier: performanceState.hypotheticalGrowthMultiplier,
				displayCurrency: this.displayCurrency,
				plotOptions: {
					series: {
						lineWidth: 1.3
					}
				},
				yAxis: {
					title: {
						text: this.yAxisLabel
					}
				},
				multiSeriesConfig: CommonChartUtil.createCustomisationsArray(
					ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS
				),
				legend: {
					enabled: false
				}
			};
		}

		if (asOfDate) {
			panels.push(
				new StaticParagraphComponent(
					'PDF_HYPO_PERF_CHART_EXPLANATION',
					'Hypothetical Initial Investment: {0}{1}',
					{
						0: this.displayCurrency,
						1: NumberUtil.commaAndDecimalNumberFormatter(performanceState.hypotheticalGrowthMultiplier)
					},
					{ paddingBottom: 20 }
				)
			);
		}
		const earliestDataPointDate = CellUtil.getById(tabData[0]?.tableData[0]?.cells[0], 'intervalStopDate')?.rawValue as string;
		const earliestDataPoint = earliestDataPointDate ? dayjs.utc(earliestDataPointDate, 'YYYY-MM-DD') : null;
		const startDate = performanceState.hypotheticalGrowthStartTimestamp ? dayjs(performanceState.hypotheticalGrowthStartTimestamp) : earliestDataPoint;
		const updatedTabs = startDate?.isValid() ? tabData.map(tab => HypotheticalGrowthValueService.prepareAccumulativeReturns(tab, startDate)) : tabData;

		panels.push(
			new HighchartsSVGComponent(
				this.name,
				{ height: 220, paddingBottom: this.isCustomFeeEnabled ? 0 : 23 },
				updatedTabs,
				ChartType.Line,
				customAttributes
			)
		);
		if (this.isCustomFeeEnabled) {
			panels.push(this.getLegendPanel(tabData));
		}
		panels.push(new StaticParagraphComponent(dcrKeyPrefix + 'CHART_P2', 'The growth of a hypothetical investment assumes reinvestment of dividends and capital gains.'));

		return [new ColumnPanel('Hypothetical Portfolio Chart Performance Section', panels, { marginBottom: 3 }, Alignment.Horizontal.Left)];
	}

	private getLegendPanel(tabData: TabData[]): RowPanel {
		const availableTab = tabData.filter(tab => tab);
		const resultPanels: CellPanel[] = [];
		let proportion: number[];

		switch (availableTab.length) {
			case 2:
				proportion = [25, 75];
				break;
			case 1:
			default:
				proportion = [100];
				break;
		}

		availableTab
			.forEach((tab, tabIndex) => {
				const legendPanelItems = [];
				const legendHeader =
					this.groupedLegendColumnConfiguration[tabIndex]['heading'] +
					(this.groupedLegendColumnConfiguration[tabIndex]['subHeading'] ? this.groupedLegendColumnConfiguration[tabIndex]['subHeading'] : '');
				legendPanelItems.push(
					new StaticParagraphComponent(
						`##NOT_APPLICABLE_DCR##`,
						legendHeader,
						{},
						{ paddingBottom: 5 },
						['text-bold']
					)
				);
				legendPanelItems.push(...tab.tableData
					.map((portfolio, index) => {
						const renderer = new PortfolioDotRenderer(
							portfolio.portfolioData?.[0], index, Alignment.Horizontal.Left, 100, ['text-8'], {}, 9, ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS[tabIndex], 'line'
						);
						return new RowPanel('Legend item', [renderer.render()]);
					}));
				resultPanels.push(new CellPanel(legendPanelItems, { paddingRight: 5, proportion: proportion[tabIndex] }, Alignment.Horizontal.Left));
			});

		return new RowPanel('Hypothetical Legend Panel', resultPanels, { paddingBottom: 23 });
	}
}
